import React, {useCallback, useState} from 'react'
import {Optional} from '../../../../Common/TypeHelper'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {useParams} from 'react-router-dom'
import {useAsync, useAsyncRetry} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import dayjs from 'dayjs'
import {Spacer} from '../../../../Common/Components/Spacer'
import {ContractInformationContentBlock} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractInformationContentBlock'
import {ContractInfoWithContractDate, ConvenienceStoreInfo, CvsContractInfo, MobileCarrierInfo, MyCreditCardInfo} from '../../../Props/ContractInputInfo'
import {MBContentBodyTypography, MBContentNoteWithMarkTypography, MBContentSubTitleTypography} from '../../Atoms/Typographies'
import {ExternalLinkButton} from '../../Atoms/Buttons'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {ContentBlock} from '../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../Molecules/BaseContentBlocks/ContentTitle'
import {ProcessingErrorPage} from '../Error/ProcessingErrorPage'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {CreditCardMethod, CvsMethod, FreeBucketMethod, MobileCarrierMethod} from '../../../DTO/MemberContractInfoDTO'
import {checkAsyncStateAndGetData, checkAsyncStateAndGetDataOrError, isError, memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {useMBMemberErrorUnknown} from '../../Hooks/UseMBMemberError'
import {PIMAccentButton} from '../../Atoms/Buttons/Bases/PIMAccentButton'
import {MBLoadingOverlay} from '../../Molecules/MBLoadingOverlay'
import {MBCvsPaymentDialog} from '../../Molecules/MBDialogs'
import {Contract} from '../../../Utils/Resources'
import STATUS = Contract.STATUS

const pageTitle = '契約情報'

type MembersEditContractInfoPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

export const MembersEditContractInfoPage: React.VFC<MembersEditContractInfoPageProps> = (
  {
    navigator,
    memberRepo,
  },
) => {
  const {bucketKey} = useParams<{ bucketKey: string }>()

  const [openCvsPaymentDialog, setOpenCvsPaymentDialog] = useState(false)
  const [openLoading, setOpenLoading] = useState(false)

  const throwMBMemberError = useMBMemberErrorUnknown()

  const resultBucketInfo = useAsync(async () => memberRepo.getBucketInfoByBucketKey(bucketKey))
  const resultContractInfo = useAsyncRetry(
    async () => memberRepo.getContractInfoByBucketKey(bucketKey),
  )

  const goToEditContractInputPage = useCallback(() => {
    navigator.goToEditContractInputPage(bucketKey)
  }, [navigator])

  const handleCvsPaymentDialogOkButtonClicked = useCallback(async (
    cvsInfo: ConvenienceStoreInfo,
  ) => {
    if (cvsInfo.convenienceStoreKey === '') return
    setOpenCvsPaymentDialog(false)
    setOpenLoading(true)

    try {
      const result = await memberRepo.postContinuanceCvs(bucketKey, cvsInfo.convenienceStoreKey)
      memberResponseStatusCheck(result.status)
      resultContractInfo.retry()
    } catch (e) {
      throwMBMemberError(e)
    }

    setOpenLoading(false)
  }, [memberRepo, throwMBMemberError, resultContractInfo])

  if (resultBucketInfo.loading || resultContractInfo.loading) return null

  const bucketInfo = checkAsyncStateAndGetData(resultBucketInfo)
  const contractInfo = checkAsyncStateAndGetDataOrError(resultContractInfo)
  if (isError(contractInfo)) {
    if (contractInfo.message === 'Processing') {
      return <ProcessingErrorPage
        pageTitle={pageTitle}
        bucketInfo={bucketInfo}
        buttonName={bucketInfo.fcMyPageInfo.name}
        url={bucketInfo.fcMyPageInfo.url}
      />
    } else {
      throw contractInfo
    }
  }
  const {contractDate, now, next} = contractInfo

  if (now.contractMethod.method === 'none') {
    throw new MBMemberError('NotFound')
  }
  if (next === null) {
    throw new MBMemberError('SystemError')
  }

  const nowContractInfo: ContractInfoWithContractDate = {
    term: now.term,
    method: now.contractMethod.method,
    amount: now.amount,
    contractDate: {
      contractDate: now.startDate,
      contractEndDate: now.endDate,
    },
  }

  const nowCardInfo = getCardInfo(now.contractMethod)
  const nextCardInfo = getCardInfo(next.contractMethod)
  const nowCarrierInfo = getCarrierInfo(now.contractMethod)
  const nextCarrierInfo = getCarrierInfo(next.contractMethod)
  const nowCvsInfo = getCvsInfo(now.contractMethod)
  const nextCvsInfo = getCvsInfo(next.contractMethod)

  const cvsPaymentButton = <>{contractInfo?.next?.contractMethod.method === 'convenience_store'
    && next.status === STATUS.Reserved
    && <Spacer mt={'25px'} dataTestId={'convenience-payment-block'}>
          <PIMPageActions spacing={0.5}>
              <PIMAccentButton
                  testId={'convenience-payment-button'}
                  onClick={(): void => setOpenCvsPaymentDialog(true)}
                  disabled={
                    !contractInfo.next.contractMethod.paymentMethodDetail.isPaymentAvailableTerm
                  }
              >
                {'お支払いのコンビニを選択/確定する'}
              </PIMAccentButton>
            {!contractInfo.next.contractMethod.paymentMethodDetail.isPaymentAvailableTerm
              && contractInfo?.next?.contractMethod.paymentMethodDetail.paymentAvailableOn &&
                <MBContentNoteWithMarkTypography note={
                  contractInfo?.next?.contractMethod.paymentMethodDetail.paymentAvailableOn
                  + 'から、コンビニ支払いの手続きが可能となります。'}/>}
          </PIMPageActions>
      </Spacer>}

    <MBLoadingOverlay open={openLoading}/>
    <MBCvsPaymentDialog
      open={openCvsPaymentDialog}
      onCancelButtonClick={(): void => setOpenCvsPaymentDialog(false)}
      onOkButtonClick={handleCvsPaymentDialogOkButtonClicked}
      defaultConvenienceStoreKey={nextCvsInfo?.convenienceStoreKey}
    /></>

  return (
    <MembersPageTemplate
      hideFooterNavi={true}
      bucketInfo={bucketInfo}
    >
      <PageTitle>{pageTitle}</PageTitle>

      <ContentBlock>
        <MBContentBodyDivider/>
        <Spacer mt={'18px'} mb={'5px'}>
          <MBContentSubTitleTypography>継続期間</MBContentSubTitleTypography>
        </Spacer>
        <Spacer mt={'5px'} mb={'18px'}>
          <MBContentBodyTypography>{makeDurationString(contractDate)}</MBContentBodyTypography>
        </Spacer>
        <MBContentBodyDivider/>
      </ContentBlock>

      <ContractInformationContentBlock
        title={'現在の契約情報'}
        contractInfoWithContractDate={nowContractInfo}
        cardInfo={nowCardInfo}
        carrierInfo={nowCarrierInfo}
        cvsInfo={nowCvsInfo}
        dataTestId={'block-now-contract-info'}
      />

      {
        (next.status === STATUS.Canceled)
          ? <LeavingMessageContent/>
          : <ContractInformationContentBlock
            title={'次回の契約情報'}
            contractInfoWithContractDate={{
              term: next.term,
              method: next.contractMethod.method,
              amount: next.amount,
              contractDate: {
                contractDate: next.startDate,
                contractEndDate: next.endDate,
              },
            }}
            cardInfo={nextCardInfo}
            carrierInfo={nextCarrierInfo}
            cvsInfo={nextCvsInfo}
            editButtonProps={{
              buttonName: 'お支払いに関する情報変更',
              onClick: (): void => goToEditContractInputPage(),
              disabled: !(next.status === STATUS.Reserved),
            }}
            cvsPaymentButton={cvsPaymentButton}
            dataTestId={'block-next-contract-info'}
          />
      }

      <Spacer mt={'30px'}>
        <PIMPageActions>
          <ExternalLinkButton
            label={`${bucketInfo.fcMyPageInfo.name ?? ''}に移動する`}
            url={bucketInfo.fcMyPageInfo.url}
          />
        </PIMPageActions>
      </Spacer>

    </MembersPageTemplate>
  )
}

const makeDurationString = (signupDate: string): string => {
  const MONTHS_IN_A_YEAR = 12
  const dateTo = dayjs().startOf('month')
  const dateFrom = dayjs(signupDate).startOf('month')

  const durationYear = dateTo.diff(dateFrom, 'year')
  const durationMonth = dateTo.diff(dateFrom, 'month') % MONTHS_IN_A_YEAR

  return `${durationYear} 年 ${durationMonth} か月`
}

const LeavingMessageContent = (): JSX.Element => {
  return (
    <ContentBlock data-testid={'block-next-contract-info'}>
      <ContentTitle noMarginBottom={true}>次回の契約情報</ContentTitle>
      <Spacer mt={'15px'} mb={'18px'}>
        <MBContentBodyTypography>退会手続き済み</MBContentBodyTypography>
      </Spacer>
      <MBContentBodyDivider/>
    </ContentBlock>
  )
}

const getCardInfo = (
  contractMethod: CreditCardMethod | MobileCarrierMethod | CvsMethod | FreeBucketMethod,
):
  Optional<MyCreditCardInfo> => {
  if (contractMethod.method === 'credit_card') {
    return contractMethod.paymentMethodDetail
  }
  return undefined
}

const getCarrierInfo = (
  contractMethod: CreditCardMethod | MobileCarrierMethod | CvsMethod | FreeBucketMethod,
):
  Optional<MobileCarrierInfo> => {
  if (contractMethod.method === 'mobile_carrier') {
    return contractMethod.paymentMethodDetail
  }
  return undefined
}

const getCvsInfo = (
  contractMethod: CreditCardMethod | MobileCarrierMethod | CvsMethod | FreeBucketMethod,
): Optional<CvsContractInfo> => {
  return contractMethod.method === 'convenience_store'
    ? contractMethod.paymentMethodDetail
    : undefined
}
